import { getLayer, updateLayer } from '../core/model/document';
import type { Layer, PaintLayer } from '../core/model/types';
import { textBox } from '../core/paint/text';
import { fillLayerDataUrl } from '../io/raster';
import { textLayerToDataUrl } from '../io/textRaster';
import type { AppContext } from './context';
import { refuseMultiSelection } from './context';

/**
 * Rasterise a layer: turn the pixels it *describes* into pixels it *has*.
 *
 * A text layer is a paragraph of parameters — a font, a size, a colour — and a
 * fill layer is one colour. Neither has a raster, and a raster is what a mask
 * needs: a mask is a stencil in the layer's own pixel space, and a layer
 * described by parameters has no pixel space to cut a stencil in.
 *
 * So the honest way to mask a text layer is to bake it first, and to bake it
 * on purpose: the user asks for it, the command says what it costs (the text
 * stops being editable), and undo brings the text back with its words intact.
 *
 * The ink comes from the shared renderers — `io/textRaster` for text, the same
 * one the export and the merges use — so a rasterised layer looks exactly like
 * the live one did.
 */

export type RasteriseRejection = 'no-layer' | 'already-pixels' | 'unsupported' | 'locked';

const REJECTION_MESSAGES: Record<RasteriseRejection, string> = {
  'no-layer': 'There is no layer to rasterise.',
  'already-pixels': 'That layer is already pixels.',
  unsupported: 'That layer type cannot be rasterised.',
  locked: 'That layer is locked.',
};

/** Can this layer be rasterised at all? */
export function canRasterise(layer: Layer): boolean {
  return layer.type === 'text' || layer.type === 'fill';
}

/**
 * The raster a layer would become, or the reason it cannot.
 *
 * The raster is in the layer's **own** pixel space — unrotated, unscaled —
 * exactly like a mask's. The layer keeps its position, scale and angle, so
 * baking it does not move anything on the canvas.
 */
export function rasteriseLayer(
  layer: Layer,
  doc: { width: number; height: number },
): {
  pixels: string;
  naturalWidth: number;
  naturalHeight: number;
} | null {
  if (layer.type === 'text') {
    const box = textBox(layer);
    return {
      pixels: textLayerToDataUrl(layer),
      naturalWidth: Math.max(1, Math.round(box.width)),
      naturalHeight: Math.max(1, Math.round(box.height)),
    };
  }
  if (layer.type === 'fill') {
    return {
      pixels: fillLayerDataUrl(layer.color, doc.width, doc.height),
      naturalWidth: doc.width,
      naturalHeight: doc.height,
    };
  }
  return null;
}

/** Rasterise the active layer, as one history entry. */
export function rasteriseActiveLayer(ctx: AppContext): void {
  // It already refuses per layer type; a set multiplies the refusals, not the
  // feature (design doc §4).
  if (refuseMultiSelection(ctx, 'Rasterise works on one layer — select just it')) return;
  const { doc, activeLayerId, commit, notify } = ctx.store.getState();
  const layer = activeLayerId ? getLayer(doc, activeLayerId) : undefined;
  if (!layer) {
    notify(REJECTION_MESSAGES['no-layer'], 'warn');
    return;
  }
  if (layer.locked) {
    notify(REJECTION_MESSAGES.locked, 'warn');
    return;
  }
  if (layer.type === 'image' || layer.type === 'paint') {
    notify(REJECTION_MESSAGES['already-pixels'], 'warn');
    return;
  }
  if (!canRasterise(layer)) {
    notify(REJECTION_MESSAGES.unsupported, 'warn');
    return;
  }
  const raster = rasteriseLayer(layer, doc);
  if (!raster) {
    notify(REJECTION_MESSAGES.unsupported, 'warn');
    return;
  }

  const baked: PaintLayer = {
    ...layer,
    type: 'paint',
    pixels: raster.pixels,
    naturalWidth: raster.naturalWidth,
    naturalHeight: raster.naturalHeight,
    // A rasterised layer has no mask: it could not have one before, and
    // baking it must not invent one.
    mask: null,
  } as PaintLayer;

  commit('Rasterise Layer', updateLayer(doc, layer.id, baked as Partial<PaintLayer>));
  notify(`“${layer.name}” rasterised — the text is no longer editable`);
}
